import { useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@mlm/ui';
import {
  getMerchantMe,
  uploadFileRequirement,
  type RequirementDef,
  type RequirementSubmission,
} from '../lib/api';

function merchantStatusVariant(
  status: string,
): 'success' | 'destructive' | 'warning' | 'secondary' {
  if (status === 'approved') return 'success';
  if (status === 'rejected' || status === 'suspended') return 'destructive';
  if (status === 'pending') return 'warning';
  return 'secondary';
}

export function DashboardPage() {
  const { data, isLoading } = useQuery({ queryKey: ['merchant-me'], queryFn: getMerchantMe });
  if (isLoading || !data) return <p className="text-muted-foreground">Loading…</p>;

  const { merchant, requirements, submissions } = data;

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{merchant.businessName}</h1>
        <Badge variant={merchantStatusVariant(merchant.status)} className="capitalize">
          {merchant.status}
        </Badge>
      </div>

      {merchant.status === 'pending' && (
        <Card>
          <CardHeader>
            <CardTitle>Awaiting approval</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>
              Your account is under review. Complete any required documents below. An admin will
              verify them and approve your store.
            </p>
          </CardContent>
        </Card>
      )}

      {(merchant.status === 'rejected' || merchant.status === 'suspended') && (
        <Card>
          <CardHeader>
            <CardTitle className="capitalize">{merchant.status}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            {merchant.rejectionReason || 'Contact the administrator for details.'}
          </CardContent>
        </Card>
      )}

      {merchant.status === 'approved' && (
        <Card>
          <CardHeader>
            <CardTitle>You're live</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Your store is approved. Add products, fulfill orders, and cash out from the sidebar.
          </CardContent>
        </Card>
      )}

      <RequirementsCard requirements={requirements} submissions={submissions} />
    </div>
  );
}

function RequirementsCard({
  requirements,
  submissions,
}: {
  requirements: RequirementDef[];
  submissions: RequirementSubmission[];
}) {
  const qc = useQueryClient();
  const byKey = new Map(submissions.map((s) => [s.requirementKey, s]));
  const upload = useMutation({
    meta: { successMessage: 'Document uploaded.' },
    mutationFn: ({ key, file }: { key: string; file: File }) => uploadFileRequirement(key, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['merchant-me'] }),
  });

  if (requirements.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Requirements</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {requirements.map((r) => {
          const sub = byKey.get(r.key);
          return (
            <div
              key={r.key}
              className="flex items-start justify-between gap-4 border-b pb-3 last:border-0"
            >
              <div>
                <div className="text-sm font-medium">
                  {r.label}
                  {r.required && <span className="text-destructive"> *</span>}
                </div>
                {r.description && <p className="text-xs text-muted-foreground">{r.description}</p>}
                {sub && (
                  <Badge
                    variant={
                      sub.status === 'approved'
                        ? 'success'
                        : sub.status === 'rejected'
                          ? 'destructive'
                          : 'secondary'
                    }
                    className="mt-1 capitalize"
                  >
                    {sub.status}
                  </Badge>
                )}
                {sub?.rejectionReason && (
                  <p className="text-xs text-destructive mt-1">{sub.rejectionReason}</p>
                )}
              </div>
              {r.acceptsFile ? (
                <FileUpload
                  onPick={(file) => upload.mutate({ key: r.key, file })}
                  busy={upload.isPending}
                  done={!!sub?.fileKey}
                />
              ) : (
                <span className="text-xs text-muted-foreground">
                  {sub?.textValue ? 'Submitted' : 'Provided at registration'}
                </span>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function FileUpload({
  onPick,
  busy,
  done,
}: {
  onPick: (f: File) => void;
  busy: boolean;
  done: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <div className="shrink-0">
      <input
        ref={ref}
        type="file"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          e.target.value = '';
        }}
      />
      <Button variant="outline" size="sm" disabled={busy} onClick={() => ref.current?.click()}>
        {busy ? 'Uploading…' : done ? 'Replace file' : 'Upload'}
      </Button>
    </div>
  );
}
